/**
 * Content Pages
 * About, Contact, Reviews, Gallery, Dashboard, and Brands pages
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Cat, Heart, Shield, Users, MapPin, Phone, Mail, Send, CheckCircle, 
  Star, Calendar, ArrowRight, X 
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import LoginStreakBadge from "../components/LoginStreakBadge";

import { useAuth } from "../contexts/AuthContext";
import { API } from "../lib/api";

const AboutPage = () => (
  <div className="min-h-screen pt-36 bg-stone-50" data-testid="about-page">
    <div className="max-w-6xl mx-auto px-6 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">About CatPals+</h1>
        <p className="text-stone-600 max-w-2xl mx-auto">Norwich's trusted cat rescue and sitting service since 2019</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
        <img src="https://images.unsplash.com/photo-1597560906461-9da6185bc89f?w=800" alt="Cat rescue team" className="rounded-2xl shadow-lg" />
        <div>
          <h2 className="text-3xl font-bold text-stone-900 mb-4">Our Story</h2>
          <p className="text-stone-600 leading-relaxed mb-4">
            CatPals+ was founded in 2019 by a group of passionate cat lovers in Norwich who saw a need for 
            both rescue services and professional cat care in the community.
          </p>
          <p className="text-stone-600 leading-relaxed mb-4">
            What started as a small foster network has grown into Norwich's most trusted cat rescue and 
            sitting service. We've helped over 200 cats find their forever homes and provide daily care 
            for dozens of feline friends.
          </p>
          <p className="text-stone-600 leading-relaxed">
            Our team is made up of experienced cat handlers, veterinary technicians, and dedicated 
            volunteers who share one thing in common: an unwavering love for cats.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {[
          { icon: Heart, title: "Our Mission", desc: "To ensure every cat in Norwich has a safe, loving home and the best possible care." },
          { icon: Shield, title: "Our Promise", desc: "Every cat is treated like family. We provide veterinary care, love, and patience." },
          { icon: Users, title: "Our Team", desc: "A dedicated group of cat lovers, trained professionals, and caring volunteers." }
        ].map(({ icon: Icon, title, desc }) => (
          <Card key={title} className="text-center p-6">
            <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon className="w-7 h-7 text-amber-600" />
            </div>
            <h3 className="text-xl font-semibold text-stone-900 mb-2">{title}</h3>
            <p className="text-stone-600">{desc}</p>
          </Card>
        ))}
      </div>
    </div>
  </div>
);

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message sent successfully!");
    } catch {
      toast.error("Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-36 bg-stone-50" data-testid="contact-page">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">Get in Touch</h1>
          <p className="text-stone-600 max-w-2xl mx-auto">Have questions about adoption or our services? We'd love to hear from you!</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <Card>
            <CardHeader>
              <CardTitle>Send us a Message</CardTitle>
            </CardHeader>
            <CardContent>
              {sent ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Thank you!</h3>
                  <p className="text-stone-600">We'll get back to you as soon as possible.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input id="name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className="mt-1" data-testid="contact-name" />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required className="mt-1" data-testid="contact-email" />
                  </div>
                  <div>
                    <Label htmlFor="subject">Subject</Label>
                    <Select value={form.subject} onValueChange={v => setForm({ ...form, subject: v })}>
                      <SelectTrigger className="mt-1" data-testid="contact-subject">
                        <SelectValue placeholder="Select a subject" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="adoption">Cat Adoption Inquiry</SelectItem>
                        <SelectItem value="sitting">Cat Sitting Question</SelectItem>
                        <SelectItem value="volunteer">Volunteering</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="message">Message</Label>
                    <Textarea id="message" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} required rows={5} className="mt-1" data-testid="contact-message" />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-amber-600 hover:bg-amber-700 rounded-full" data-testid="contact-submit">
                    {loading ? "Sending..." : <><Send className="w-4 h-4 mr-2" /> Send Message</>}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg mb-4">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium">Address</p>
                      <p className="text-stone-600 text-sm">8 Harvey Lane, Norwich,<br />Norfolk, NR70BQ</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <Phone className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium">Phone</p>
                      <p className="text-stone-600 text-sm">Landline: (01603) 555574</p>
                      <p className="text-stone-600 text-sm">Mobile: (07754) 754758</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <Mail className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium">Email</p>
                      <p className="text-stone-600 text-sm">Team@CatPals.co.uk</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg mb-4">Opening Hours</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span>Monday - Friday</span><span className="text-stone-600">9:00 AM - 6:00 PM</span></div>
                  <div className="flex justify-between"><span>Saturday</span><span className="text-stone-600">10:00 AM - 4:00 PM</span></div>
                  <div className="flex justify-between"><span>Sunday</span><span className="text-stone-600">By appointment</span></div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    service_type: "cat_sitting",
    rating: 5,
    title: "",
    review_text: ""
  });

  useEffect(() => {
    axios.get(`${API}/reviews`)
      .then(r => setReviews(r.data))
      .catch(() => toast.error("Failed to load reviews"))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.title || !form.review_text) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, form);
      toast.success("Thank you for your review! It will appear once approved.");
      setForm({ name: "", email: "", service_type: "cat_sitting", rating: 5, title: "", review_text: "" });
      setShowForm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  const serviceLabels = {
    cat_sitting: "Cat Sitting",
    scan_angel: "Scan Angel",
    adoption: "Adoption",
    friend: "Friend of CatPals+",
    microchipping: "Microchipping",
    other: "Other"
  };

  return (
    <div className="min-h-screen pt-36 bg-gradient-to-br from-amber-50 via-stone-50 to-amber-50" data-testid="reviews-page">
      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Star className="w-10 h-10 text-amber-600 fill-amber-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">Reviews & Testimonials</h1>
          <p className="text-stone-600 max-w-2xl mx-auto mb-6">
            Hear from our wonderful community - customers, Scan Angel users, Friends of CatPals+, and our extended family!
          </p>
          <Button 
            onClick={() => setShowForm(!showForm)} 
            className="bg-amber-600 hover:bg-amber-700 rounded-full px-8"
            data-testid="write-review-btn"
          >
            {showForm ? "Cancel" : "Write a Review"}
          </Button>
        </div>

        {/* Review Form */}
        {showForm && (
          <Card className="max-w-2xl mx-auto mb-12 animate-fadeIn" data-testid="review-form">
            <CardHeader>
              <CardTitle>Share Your Experience</CardTitle>
              <CardDescription>We'd love to hear about your experience with CatPals+</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Your Name *</Label>
                    <Input id="name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="mt-1" required data-testid="review-name" />
                  </div>
                  <div>
                    <Label htmlFor="email">Email (optional)</Label>
                    <Input id="email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="your@email.com" className="mt-1" data-testid="review-email" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="service_type">Service/Relationship *</Label>
                    <select id="service_type" value={form.service_type} onChange={e => setForm({ ...form, service_type: e.target.value })} className="w-full mt-1 px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500" data-testid="review-service">
                      <option value="cat_sitting">Cat Sitting Customer</option>
                      <option value="scan_angel">Scan Angel Service User</option>
                      <option value="adoption">Adopted a Cat</option>
                      <option value="microchipping">Microchipping Service</option>
                      <option value="friend">Friend of CatPals+</option>
                      <option value="other">Other / Extended Family</option>
                    </select>
                  </div>
                  <div>
                    <Label>Rating *</Label>
                    <div className="flex gap-2 mt-2">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button key={star} type="button" onClick={() => setForm({ ...form, rating: star })} className="focus:outline-none" data-testid={`rating-star-${star}`}>
                          <Star className={`w-8 h-8 transition-colors ${star <= form.rating ? 'text-amber-400 fill-amber-400' : 'text-stone-300 hover:text-amber-200'}`} />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="title">Review Title *</Label>
                  <Input id="title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Summarize your experience" className="mt-1" required data-testid="review-title" />
                </div>

                <div>
                  <Label htmlFor="review_text">Your Review *</Label>
                  <Textarea id="review_text" value={form.review_text} onChange={e => setForm({ ...form, review_text: e.target.value })} placeholder="Tell us about your experience with CatPals+..." rows={5} className="mt-1" required data-testid="review-text" />
                </div>

                <Button type="submit" disabled={submitting} className="w-full bg-amber-600 hover:bg-amber-700 rounded-full py-6" data-testid="submit-review-btn">
                  {submitting ? "Submitting..." : "Submit Review"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Reviews Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map(i => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-4 bg-stone-200 rounded w-1/4 mb-4" />
                  <div className="h-6 bg-stone-200 rounded w-3/4 mb-2" />
                  <div className="h-20 bg-stone-200 rounded mb-4" />
                  <div className="h-4 bg-stone-200 rounded w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : reviews.length === 0 ? (
          <Card className="max-w-md mx-auto text-center p-12">
            <Star className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-stone-700 mb-2">No Reviews Yet</h3>
            <p className="text-stone-500 mb-4">Be the first to share your experience with CatPals+!</p>
            <Button onClick={() => setShowForm(true)} className="bg-amber-600 hover:bg-amber-700 rounded-full">
              Write the First Review
            </Button>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {reviews.map((review, index) => (
              <Card key={review.review_id} className="hover:shadow-lg transition-shadow animate-slideUp" style={{ animationDelay: `${index * 0.1}s` }} data-testid={`review-card-${review.review_id}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <Badge className="bg-amber-100 text-amber-700">{serviceLabels[review.service_type] || "Review"}</Badge>
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map(star => (
                        <Star key={star} className={`w-4 h-4 ${star <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-stone-300'}`} />
                      ))}
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-stone-900 mb-2">"{review.title}"</h3>
                  <p className="text-stone-600 mb-4 leading-relaxed">{review.review_text}</p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-stone-700">— {review.name}</span>
                    <span className="text-stone-400">{new Date(review.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <Card className="bg-green-900 text-white p-8 rounded-2xl">
            <h2 className="text-2xl font-bold mb-4">Have You Used Our Services?</h2>
            <p className="text-green-100 mb-6 max-w-xl mx-auto">
              Whether you're a cat sitting customer, Scan Angel user, or part of our extended CatPals+ family, 
              we'd love to hear from you! Your feedback helps us improve and helps others find us.
            </p>
            <Button onClick={() => { setShowForm(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-8">
              Share Your Story
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    location: "",
    bio: "",
    pet_type: "cats",
    num_cats: 0,
    num_dogs: 0,
    num_other_pets: 0,
    other_pets_description: "",
    interested_in_house_sitting: false,
    pet_photo_base64: null,
    pet_photo_mime_type: null
  });
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    const fetchData = async () => {
      // Fetch bookings - SECURITY: Use httpOnly cookies for auth
      try {
        const res = await axios.get(`${API}/bookings`, { withCredentials: true });
        setBookings(res.data);
      } catch (err) {
        if (err.response?.status !== 401) {
          console.error("Failed to load bookings:", err);
        }
      } finally {
        setLoading(false);
      }

      // Fetch profile
      try {
        const profileRes = await axios.get(`${API}/profile`, { withCredentials: true });
        setProfile(profileRes.data);
        setProfileForm({
          location: profileRes.data.location || "",
          bio: profileRes.data.bio || "",
          pet_type: profileRes.data.pet_type || "cats",
          num_cats: profileRes.data.num_cats || 0,
          num_dogs: profileRes.data.num_dogs || 0,
          num_other_pets: profileRes.data.num_other_pets || 0,
          other_pets_description: profileRes.data.other_pets_description || "",
          interested_in_house_sitting: profileRes.data.interested_in_house_sitting || false,
          pet_photo_base64: profileRes.data.pet_photo_base64 || null,
          pet_photo_mime_type: profileRes.data.pet_photo_mime_type || null
        });
      } catch (err) {
        console.error("Failed to load profile:", err);
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchData();
  }, [user, navigate]);

  const handleProfileSave = async () => {
    setSavingProfile(true);
    try {
      const res = await axios.put(`${API}/profile`, profileForm, {
        withCredentials: true
      });
      setProfile(res.data);
      setShowProfileEdit(false);
      toast.success("Profile updated successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePetPhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image too large. Maximum size is 2MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(",")[1];
      setProfileForm(prev => ({
        ...prev,
        pet_photo_base64: base64,
        pet_photo_mime_type: file.type
      }));
    };
    reader.readAsDataURL(file);
  };

  const removePetPhoto = () => {
    setProfileForm(prev => ({
      ...prev,
      pet_photo_base64: null,
      pet_photo_mime_type: null
    }));
  };

  const getStatusColor = (status) => {
    const colors = { pending: "bg-yellow-100 text-yellow-700", confirmed: "bg-green-100 text-green-700", completed: "bg-blue-100 text-blue-700", cancelled: "bg-red-100 text-red-700" };
    return colors[status] || "bg-stone-100";
  };

  if (!user) return null;

  return (
    <div className="min-h-screen pt-36 bg-stone-50" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Login Streak Badge */}
        <div className="mb-8">
          <LoginStreakBadge />
        </div>

        {/* My Profile Section */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-stone-900">My Profile</h2>
              <p className="text-stone-600">Manage your personal information</p>
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowProfileEdit(!showProfileEdit)}
              data-testid="edit-profile-btn"
            >
              {showProfileEdit ? <><X className="w-4 h-4 mr-2" /> Cancel</> : "Edit Profile"}
            </Button>
          </div>

          {loadingProfile ? (
            <Card className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full mx-auto" />
            </Card>
          ) : showProfileEdit ? (
            <Card className="p-6" data-testid="profile-edit-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    placeholder="e.g., Norwich, Norfolk"
                    value={profileForm.location}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, location: e.target.value }))}
                    className="mt-1"
                    data-testid="profile-edit-location"
                  />
                </div>
                <div>
                  <Label htmlFor="pet_type">Pet Type</Label>
                  <Select 
                    value={profileForm.pet_type} 
                    onValueChange={(v) => setProfileForm(prev => ({ ...prev, pet_type: v }))}
                  >
                    <SelectTrigger className="mt-1" data-testid="profile-edit-pet-type">
                      <SelectValue placeholder="Select pet type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cats">Cats</SelectItem>
                      <SelectItem value="dogs">Dogs</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                      <SelectItem value="none">No pets</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="bio">About Me</Label>
                  <Textarea
                    id="bio"
                    placeholder="Tell us about yourself..."
                    value={profileForm.bio}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, bio: e.target.value }))}
                    className="mt-1"
                    rows={3}
                    data-testid="profile-edit-bio"
                  />
                </div>
                <div>
                  <Label htmlFor="num_cats">Number of Cats</Label>
                  <Input
                    id="num_cats"
                    type="number"
                    min="0"
                    max="50"
                    value={profileForm.num_cats}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, num_cats: parseInt(e.target.value) || 0 }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="num_dogs">Number of Dogs</Label>
                  <Input
                    id="num_dogs"
                    type="number"
                    min="0"
                    max="50"
                    value={profileForm.num_dogs}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, num_dogs: parseInt(e.target.value) || 0 }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="num_other">Other Pets</Label>
                  <Input
                    id="num_other"
                    type="number"
                    min="0"
                    max="50"
                    value={profileForm.num_other_pets}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, num_other_pets: parseInt(e.target.value) || 0 }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="other_desc">Other Pets Description</Label>
                  <Input
                    id="other_desc"
                    placeholder="e.g., rabbits, hamsters..."
                    value={profileForm.other_pets_description}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, other_pets_description: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                
                {/* Pet Photo Upload */}
                <div className="md:col-span-2">
                  <Label>Pet Photo</Label>
                  <div className="mt-2 flex items-start gap-4">
                    {profileForm.pet_photo_base64 ? (
                      <div className="relative">
                        <img
                          src={`data:${profileForm.pet_photo_mime_type || 'image/jpeg'};base64,${profileForm.pet_photo_base64}`}
                          alt="Pet"
                          className="w-32 h-32 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={removePetPhoto}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-32 h-32 bg-stone-100 rounded-lg flex items-center justify-center">
                        <Cat className="w-12 h-12 text-stone-300" />
                      </div>
                    )}
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handlePetPhotoUpload}
                        className="hidden"
                        id="pet-photo-input"
                        data-testid="pet-photo-input"
                      />
                      <Label htmlFor="pet-photo-input" className="cursor-pointer">
                        <div className="px-4 py-2 border border-dashed border-stone-300 rounded-lg text-center hover:border-amber-500 transition-colors">
                          <p className="text-sm text-stone-600">Click to upload a photo of your pet</p>
                          <p className="text-xs text-stone-400 mt-1">Max 2MB - JPEG, PNG, GIF, WebP</p>
                        </div>
                      </Label>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2 flex justify-end gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={() => setShowProfileEdit(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleProfileSave} 
                    disabled={savingProfile}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="save-profile-btn"
                  >
                    {savingProfile ? "Saving..." : "Save Profile"}
                  </Button>
                </div>
              </div>
            </Card>
          ) : (
            <Card className="p-6" data-testid="profile-view">
              <div className="flex items-start gap-6">
                {/* Profile Photo / Pet Photo */}
                <div className="flex-shrink-0">
                  {profile?.pet_photo_base64 ? (
                    <img
                      src={`data:${profile.pet_photo_mime_type || 'image/jpeg'};base64,${profile.pet_photo_base64}`}
                      alt="Pet"
                      className="w-24 h-24 object-cover rounded-xl"
                    />
                  ) : (
                    <div className="w-24 h-24 bg-amber-100 rounded-xl flex items-center justify-center">
                      <Cat className="w-12 h-12 text-amber-600" />
                    </div>
                  )}
                </div>
                
                {/* Profile Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold">{user.name}</h3>
                    {profile?.profile_completed ? (
                      <Badge className="bg-green-100 text-green-700">Profile Complete</Badge>
                    ) : (
                      <Badge className="bg-yellow-100 text-yellow-700">Profile Incomplete</Badge>
                    )}
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-stone-600">
                      <Mail className="w-4 h-4" />
                      {user.email}
                    </div>
                    {profile?.location && (
                      <div className="flex items-center gap-2 text-stone-600">
                        <MapPin className="w-4 h-4" />
                        {profile.location}
                      </div>
                    )}
                  </div>
                  
                  {profile?.bio && (
                    <p className="mt-3 text-stone-600">{profile.bio}</p>
                  )}
                  
                  {/* Pet Stats */}
                  {profile?.profile_completed && (
                    <div className="flex gap-4 mt-4">
                      {profile.num_cats > 0 && (
                        <div className="flex items-center gap-1 text-amber-600">
                          <Cat className="w-4 h-4" />
                          <span className="font-medium">{profile.num_cats} {profile.num_cats === 1 ? 'cat' : 'cats'}</span>
                        </div>
                      )}
                      {profile.num_dogs > 0 && (
                        <div className="flex items-center gap-1 text-blue-600">
                          <Heart className="w-4 h-4" />
                          <span className="font-medium">{profile.num_dogs} {profile.num_dogs === 1 ? 'dog' : 'dogs'}</span>
                        </div>
                      )}
                      {profile.num_other_pets > 0 && (
                        <div className="flex items-center gap-1 text-green-600">
                          <Star className="w-4 h-4" />
                          <span className="font-medium">{profile.num_other_pets} other</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* My Bookings Section */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-stone-900">My Bookings</h2>
            <p className="text-stone-600">Manage your cat sitting reservations</p>
          </div>
          <Button onClick={() => navigate("/book")} className="bg-amber-600 hover:bg-amber-700 rounded-full" data-testid="new-booking-btn">
            <Calendar className="w-4 h-4 mr-2" /> New Booking
          </Button>
        </div>

        {loading ? (
          <Card className="p-8 text-center"><div className="animate-spin w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full mx-auto" /></Card>
        ) : bookings.length === 0 ? (
          <Card className="p-12 text-center">
            <Calendar className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No bookings yet</h3>
            <p className="text-stone-500 mb-6">Book your first cat sitting session!</p>
            <Button onClick={() => navigate("/book")} className="bg-amber-600 hover:bg-amber-700 rounded-full">Book Now</Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {bookings.map(b => (
              <Card key={b.booking_id} className="p-6" data-testid={`booking-${b.booking_id}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-lg">{b.package_name}</h3>
                    <p className="text-stone-600">Cat: {b.cat_name}</p>
                    <p className="text-sm text-stone-500">{b.start_date} to {b.end_date}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge className={getStatusColor(b.status)}>{b.status}</Badge>
                    <Badge className={b.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>
                      {b.payment_status === "paid" ? "Paid" : "Pending Payment"}
                    </Badge>
                    <span className="font-bold text-amber-600">£{b.amount?.toFixed(2)}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const GalleryPage = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    axios.get(`${API}/gallery`)
      .then(r => setImages(r.data))
      .catch(() => toast.error("Failed to load gallery"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen pt-36 bg-gradient-to-br from-amber-50 via-stone-50 to-amber-50" data-testid="gallery-page">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Cat className="w-10 h-10 text-amber-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">Our Fur Family</h1>
          <p className="text-stone-600 max-w-2xl mx-auto text-lg">
            Meet the adorable cats we're currently looking after. Each one has their own unique personality 
            and brings joy to our lives every day.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full mx-auto" />
          </div>
        ) : images.length === 0 ? (
          <Card className="max-w-md mx-auto text-center p-8">
            <Cat className="w-16 h-16 text-amber-300 mx-auto mb-4" />
            <p className="text-stone-600">No photos yet. Check back soon!</p>
          </Card>
        ) : (
          <>
            {images.filter(img => img.featured).length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-stone-900 mb-6 flex items-center gap-2">
                  <Star className="w-6 h-6 text-amber-500 fill-amber-500" /> Featured
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {images.filter(img => img.featured).map(img => (
                    <Card key={img.image_id} className="overflow-hidden cursor-pointer group hover:shadow-xl transition-shadow" onClick={() => setSelectedImage(img)}>
                      <div className="aspect-video relative overflow-hidden">
                        {(img.image_base64 || img.image_url) ? (
                          <img 
                            src={img.image_base64 ? `data:${img.image_mime_type || 'image/jpeg'};base64,${img.image_base64}` : img.image_url} 
                            alt={img.cat_name || "Cat"} 
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full bg-stone-200 flex items-center justify-center">
                            <Cat className="w-16 h-16 text-stone-400" />
                          </div>
                        )}
                        <div className="w-full h-full bg-stone-200 flex items-center justify-center absolute inset-0 -z-10">
                          <Cat className="w-16 h-16 text-stone-400" />
                        </div>
                      </div>
                      <CardContent className="p-4">
                        {img.cat_name && <h3 className="font-bold text-lg text-stone-900">{img.cat_name}</h3>}
                        {img.caption && <p className="text-stone-600 text-sm mt-1">{img.caption}</p>}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h2 className="text-2xl font-bold text-stone-900 mb-6">All Photos</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {images.map(img => (
                  <div key={img.image_id} className="aspect-square relative overflow-hidden rounded-xl cursor-pointer group" onClick={() => setSelectedImage(img)}>
                    {(img.image_base64 || img.image_url) ? (
                      <img 
                        src={img.image_base64 ? `data:${img.image_mime_type || 'image/jpeg'};base64,${img.image_base64}` : img.image_url} 
                        alt={img.cat_name || "Cat"} 
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-stone-200 flex items-center justify-center">
                        <Cat className="w-12 h-12 text-stone-400" />
                      </div>
                    )}
                    <div className="w-full h-full bg-stone-200 flex items-center justify-center absolute inset-0 -z-10">
                      <Cat className="w-12 h-12 text-stone-400" />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                        {img.cat_name && <p className="font-medium">{img.cat_name}</p>}
                      </div>
                    </div>
                    {img.featured && (
                      <div className="absolute top-2 right-2">
                        <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {selectedImage && (
          <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
            <button className="absolute top-4 right-4 text-white hover:text-amber-400 transition-colors" onClick={() => setSelectedImage(null)}>
              <X className="w-8 h-8" />
            </button>
            <div className="max-w-4xl max-h-[90vh] relative" onClick={e => e.stopPropagation()}>
              {(selectedImage.image_base64 || selectedImage.image_url) ? (
                <img src={selectedImage.image_base64 ? `data:${selectedImage.image_mime_type || 'image/jpeg'};base64,${selectedImage.image_base64}` : selectedImage.image_url} alt={selectedImage.cat_name || "Cat"} className="max-w-full max-h-[80vh] object-contain rounded-lg" />
              ) : (
                <div className="w-96 h-96 bg-stone-200 flex items-center justify-center rounded-lg">
                  <Cat className="w-24 h-24 text-stone-400" />
                </div>
              )}
              {(selectedImage.cat_name || selectedImage.caption) && (
                <div className="mt-4 text-center text-white">
                  {selectedImage.cat_name && <h3 className="text-xl font-bold">{selectedImage.cat_name}</h3>}
                  {selectedImage.caption && <p className="text-stone-300 mt-1">{selectedImage.caption}</p>}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const BrandsPage = () => {
  const [partners] = useState([
    { id: 1, name: "Whiskas", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/9b/Whiskas_logo.svg/320px-Whiskas_logo.svg.png", description: "Premium cat food that our rescue cats love. Whiskas provides nutritional support for cats of all ages.", category: "Food & Nutrition", website: "https://www.whiskas.co.uk" },
    { id: 2, name: "Pets at Home", logo: "https://upload.wikimedia.org/wikipedia/en/thumb/5/5e/Pets_at_Home_logo.svg/320px-Pets_at_Home_logo.svg.png", description: "Our go-to supplier for pet supplies, toys, and essentials. Supporting local pet welfare initiatives.", category: "Pet Supplies", website: "https://www.petsathome.com" },
    { id: 3, name: "Royal Canin", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/37/Royal_Canin_logo.svg/320px-Royal_Canin_logo.svg.png", description: "Specialized nutrition for cats with specific dietary needs. Helping our rescue cats thrive.", category: "Food & Nutrition", website: "https://www.royalcanin.com" },
    { id: 4, name: "Catit", logo: "https://images.squarespace-cdn.com/content/v1/5e949a92e17d55230cd1d53f/1612983036158-5KP8NLRQQ6HPHP6B5MAK/Catit+Logo.png", description: "Innovative cat products including fountains, feeders, and enrichment toys that keep our cats happy.", category: "Cat Products", website: "https://www.catit.com" },
    { id: 5, name: "Feliway", logo: "https://www.feliway.com/uk/Ede6Image/feliway-logo.png", description: "Calming solutions that help our rescue cats settle into their new environments stress-free.", category: "Cat Welfare", website: "https://www.feliway.com" },
    { id: 6, name: "Trixie", logo: "https://www.trixie.de/typo3conf/ext/trixie_sitepackage/Resources/Public/Images/trixie-logo.svg", description: "Quality scratching posts, beds, and accessories that make our cattery a comfortable home.", category: "Cat Furniture", website: "https://www.trixie.de" },
    { id: 7, name: "World's Best Cat Litter", logo: "https://www.worldsbestcatlitter.com/wp-content/themes/theme-starter/images/logo.svg", description: "Made from whole-kernel corn, this eco-friendly litter offers outstanding odour control and easy clumping. A sustainable choice our cats and planet love.", category: "Cat Litter", website: "https://www.worldsbestcatlitter.com" }
  ]);

  const [showPartnerForm, setShowPartnerForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [partnerForm, setPartnerForm] = useState({
    company_name: "",
    contact_name: "",
    contact_email: "",
    website: "",
    product_category: "",
    message: ""
  });

  const categories = [...new Set(partners.map(p => p.category))];

  const handlePartnerSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.post(`${API}/partnerships`, partnerForm);
      setFormSubmitted(true);
      toast.success("Application submitted! We'll be in touch soon.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit application");
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-36 bg-gradient-to-br from-amber-50 via-stone-50 to-amber-50" data-testid="brands-page">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-16">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Heart className="w-10 h-10 text-amber-600 fill-amber-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">Brands We Love</h1>
          <p className="text-stone-600 max-w-2xl mx-auto text-lg">
            We're proud to work with amazing companies that share our passion for cat welfare. 
            These partners help us provide the best care for our rescue cats.
          </p>
        </div>

        {categories.map(category => (
          <div key={category} className="mb-16">
            <h2 className="text-2xl font-bold text-stone-900 mb-8 flex items-center gap-3">
              <span className="w-2 h-8 bg-amber-500 rounded-full"></span>
              {category}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {partners.filter(p => p.category === category).map(partner => (
                <Card key={partner.id} className="overflow-hidden hover:shadow-lg transition-all duration-300 group">
                  <CardContent className="p-6">
                    <div className="h-16 flex items-center justify-center mb-4 bg-white rounded-lg p-3">
                      <img src={partner.logo} alt={partner.name} className="max-h-full max-w-full object-contain filter group-hover:scale-105 transition-transform" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                      <div className="hidden items-center justify-center w-full h-full bg-amber-100 rounded-lg">
                        <span className="text-xl font-bold text-amber-600">{partner.name}</span>
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-stone-900 mb-2">{partner.name}</h3>
                    <p className="text-stone-600 text-sm mb-4">{partner.description}</p>
                    {partner.website && (
                      <a href={partner.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-amber-600 hover:text-amber-700 font-medium text-sm">
                        Visit Website <ArrowRight className="w-4 h-4 ml-1" />
                      </a>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}

        <Card className="bg-gradient-to-r from-amber-500 to-amber-600 text-white mt-12">
          <CardContent className="p-8 md:p-12">
            {!showPartnerForm ? (
              <div className="text-center">
                <h2 className="text-2xl md:text-3xl font-bold mb-4">Want to Partner with Us?</h2>
                <p className="text-amber-100 mb-6 max-w-2xl mx-auto">
                  We're always looking for brands that share our commitment to cat welfare. 
                  If you'd like to collaborate with CatPals+, we'd love to hear from you!
                </p>
                <Button 
                  onClick={() => setShowPartnerForm(true)}
                  className="bg-white text-amber-600 hover:bg-amber-50 rounded-full px-8"
                  data-testid="apply-partner-btn"
                >
                  <Send className="w-4 h-4 mr-2" /> Apply to Partner
                </Button>
              </div>
            ) : formSubmitted ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-white mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-2">Application Submitted!</h2>
                <p className="text-amber-100">
                  Thank you for your interest in partnering with CatPals+. We'll review your application and get back to you soon.
                </p>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">Partnership Application</h2>
                  <Button 
                    variant="ghost" 
                    className="text-white hover:bg-white/10"
                    onClick={() => setShowPartnerForm(false)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <form onSubmit={handlePartnerSubmit} className="grid md:grid-cols-2 gap-4" data-testid="partnership-form">
                  <div>
                    <Label className="text-white">Company Name *</Label>
                    <Input
                      required
                      placeholder="Your company name"
                      className="bg-white/10 border-white/30 text-white placeholder:text-white/60"
                      value={partnerForm.company_name}
                      onChange={(e) => setPartnerForm(p => ({ ...p, company_name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-white">Contact Name *</Label>
                    <Input
                      required
                      placeholder="Your name"
                      className="bg-white/10 border-white/30 text-white placeholder:text-white/60"
                      value={partnerForm.contact_name}
                      onChange={(e) => setPartnerForm(p => ({ ...p, contact_name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-white">Email *</Label>
                    <Input
                      type="email"
                      required
                      placeholder="you@company.com"
                      className="bg-white/10 border-white/30 text-white placeholder:text-white/60"
                      value={partnerForm.contact_email}
                      onChange={(e) => setPartnerForm(p => ({ ...p, contact_email: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-white">Website</Label>
                    <Input
                      placeholder="https://..."
                      className="bg-white/10 border-white/30 text-white placeholder:text-white/60"
                      value={partnerForm.website}
                      onChange={(e) => setPartnerForm(p => ({ ...p, website: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-white">Product Category *</Label>
                    <Input
                      required
                      placeholder="e.g., Cat Food, Toys, Health..."
                      className="bg-white/10 border-white/30 text-white placeholder:text-white/60"
                      value={partnerForm.product_category}
                      onChange={(e) => setPartnerForm(p => ({ ...p, product_category: e.target.value }))}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-white">Tell us about your brand *</Label>
                    <Textarea
                      required
                      rows={4}
                      placeholder="What makes your brand special? How do you think we could work together?"
                      className="bg-white/10 border-white/30 text-white placeholder:text-white/60"
                      value={partnerForm.message}
                      onChange={(e) => setPartnerForm(p => ({ ...p, message: e.target.value }))}
                    />
                  </div>
                  <div className="md:col-span-2 flex justify-end">
                    <Button 
                      type="submit" 
                      disabled={formLoading}
                      className="bg-white text-amber-600 hover:bg-amber-50 rounded-full px-8"
                      data-testid="submit-partnership-btn"
                    >
                      {formLoading ? "Submitting..." : "Submit Application"}
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-stone-500 text-sm mt-8">
          Partner logos and trademarks are property of their respective owners. 
          Listing does not imply endorsement or sponsorship unless explicitly stated.
        </p>
      </div>
    </div>
  );
};

export { AboutPage, ContactPage, ReviewsPage, DashboardPage, GalleryPage, BrandsPage };
